import { SalesPoint, SalesPointType } from 'orderme-api-integration-client';
import { SalePointClient } from '../../helpers/client';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, styled } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Input, Label } from '../../controls';
import { useImperativeHandle, forwardRef } from 'react';

const LockerInput = styled(Input)({
  minWidth: '250px',
});

type LockerSelectorProps = {
  onFound: (locker: SalesPoint) => void;
  onNotFound: () => void;
};

export interface LockerSelectorRef {
  clear: () => void;
}

export const LockerSelector = forwardRef<LockerSelectorRef, LockerSelectorProps>(({ onFound, onNotFound }, ref) => {
  const salePointClient: SalePointClient = new SalePointClient();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState<boolean | null>(null);
  const [locker, setLocker] = useState<SalesPoint | null>(null);
  const { t } = useTranslation(['salesPoint']);

  useImperativeHandle(ref, () => ({
    clear: () => {
      setCode('');
      setFound(null);
      setLocker(null);
    },
  }));

  const search = async () => {
    try {
      setBusy(true);
      setFound(null);
      setLocker(null);

      const num = Number(code);
      if (!num) {
        setFound(false);
        onNotFound?.();
        return;
      }

      const result = await salePointClient.getByTypeAndCode(num, SalesPointType.Wardrobe);
      if (result) {
        setLocker(result);
        setFound(true);
        onFound(result);
      } else {
        setFound(false);
        onNotFound?.();
      }
    } catch {
      setFound(false);
      onNotFound?.();
    } finally {
      setBusy(false);
    }
  };

  const infoText = locker ? `${locker.name} (${locker.address} ${locker.city})` : '';

  return (
    <>
      <Grid container sx={{ alignItems: 'center', maxWidth: '500px' }} spacing={2}>
        <Grid size={{ xs: 12, sm: 'grow' }}>
          <LockerInput placeholder={t('salesPoint:enterBusinessLockerNo')} onChange={setCode} value={code} />
        </Grid>
        <Grid size={{ xs: 12, sm: 'auto' }}>
          <Button disabled={busy || !code} variant="outlined" onClick={search}>
            {t('salesPoint:search')}
          </Button>
        </Grid>
      </Grid>
      <Label
        sx={{ textAlign: 'left', width: '100%', mt: '10px' }}
        isVisible={found !== null}
        type={found ? 'success' : 'error'}
        content={
          found
            ? t('salesPoint:selectedBusinessLocker', { wardrobe: infoText })
            : t('salesPoint:businessLockerNotFound')
        }
      />
    </>
  );
});
